'use client';

import { useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import axiosInstance, { endpoints } from 'src/lib/axios';
import { formulärDialogProps } from 'src/components/kortkommandon/formulär-dialog';
import { toast } from 'src/components/snackbar';
import type { Felpost } from 'src/lib/fel/felbutik';

type Props = {
  öppen: boolean;
  fel: Felpost | null;
  vidStängning: () => void;
};

/**
 * Låter användaren berätta vad som hände. Felet är redan lagrat på servern när den här dialogen
 * öppnas — det som saknas är det bara människan vet: vad hen försökte göra.
 *
 * Ingenting skickas förrän användaren trycker. Det är hela poängen med att fråga.
 */
export function FelrapportDialog({ öppen, fel, vidStängning }: Props) {
  const [vadJagGjorde, setVadJagGjorde] = useState('');
  const [vadHände, setVadHände] = useState('');
  const [filer, setFiler] = useState<File[]>([]);
  const [skickar, setSkickar] = useState(false);
  const [visaTekniskt, setVisaTekniskt] = useState(false);

  const referens = fel?.referens;

  const stäng = () => {
    setVadJagGjorde('');
    setVadHände('');
    setFiler([]);
    setVisaTekniskt(false);
    vidStängning();
  };

  const skicka = async () => {
    // Cmd+Enter går förbi en disabled knapp — villkoren måste därför stå även här.
    if (skickar || !referens || !vadHände.trim()) {
      return;
    }
    setSkickar(true);
    try {
      const formData = new FormData();
      formData.append(
        'rapport',
        new Blob([JSON.stringify({ vadJagGjorde, vadHände })], { type: 'application/json' })
      );
      filer.forEach((fil) => formData.append('filer', fil));

      await axiosInstance.post(endpoints.fel.rapport(referens), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(`Tack — din rapport har referens ${referens}`);
      stäng();
    } catch {
      // Interceptorn har redan visat felet. Att toasta igen vore att skylla på användaren.
      setSkickar(false);
    }
  };

  const kanSkicka = Boolean(referens) && vadHände.trim().length > 0 && !skickar;

  return (
    <Dialog
      open={öppen}
      onClose={stäng}
      maxWidth="sm"
      fullWidth
      slotProps={formulärDialogProps(skicka)}
    >
      <DialogTitle>Rapportera problemet</DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Något gick fel, och vi har redan sparat den tekniska informationen. Det vi inte kan se
            är vad du försökte göra — berätta gärna, så blir felet mycket lättare att rätta.
          </Typography>

          {referens ? (
            <Alert severity="info" variant="outlined">
              Referens: <strong>{referens}</strong>
            </Alert>
          ) : (
            <Alert severity="warning" variant="outlined">
              Det här felet saknar referens, troligen för att anropet aldrig nådde servern. Prova
              igen när du har uppkoppling.
            </Alert>
          )}

          <TextField
            label="Vad försökte du göra?"
            placeholder="Jag skulle bokföra en leverantörsfaktura…"
            value={vadJagGjorde}
            onChange={(e) => setVadJagGjorde(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />

          <TextField
            label="Vad hände?"
            placeholder="Sidan blev tom och det kom upp en röd ruta."
            value={vadHände}
            onChange={(e) => setVadHände(e.target.value)}
            multiline
            rows={3}
            fullWidth
            required
          />

          <Button component="label" variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
            Bifoga skärmbild
            <input
              type="file"
              hidden
              multiple
              accept="image/*,application/pdf"
              onChange={(e) => setFiler(Array.from(e.target.files ?? []))}
            />
          </Button>

          {filer.length > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {filer.map((f) => f.name).join(', ')}
            </Typography>
          )}

          <Box>
            <Link
              component="button"
              type="button"
              variant="caption"
              underline="always"
              onClick={() => setVisaTekniskt((v) => !v)}
              sx={{ color: 'text.secondary' }}
            >
              {visaTekniskt ? 'Dölj' : 'Visa'} vilken teknisk information som skickas med
            </Link>
            <Collapse in={visaTekniskt}>
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.neutral',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {[
                  `Referens: ${referens ?? '—'}`,
                  `Tidpunkt: ${fel?.tidpunkt?.toLocaleString('sv-SE') ?? '—'}`,
                  `Sida: ${fel?.sökväg ?? '—'}`,
                  fel?.anrop ? `Anrop: ${fel.anrop}` : null,
                  fel?.status ? `Status: ${fel.status}` : null,
                  '',
                  'Serverns felmeddelande och stacktrace är redan sparade och skickas inte',
                  'härifrån. Personuppgifter maskeras innan de lagras.',
                ]
                  .filter(Boolean)
                  .join('\n')}
              </Box>
            </Collapse>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={stäng} color="inherit">
          Avbryt
        </Button>
        <Button type="submit" variant="contained" disabled={!kanSkicka}>
          {skickar ? 'Skickar…' : 'Skicka rapport'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
